import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { navGroupsFor, isNavActive } from "@/components/admin/nav";
import { useGuardedNavigate } from "@/components/admin/DirtyGuard";
import { useAuth } from "@/context/AuthContext";

/**
 * Grouped admin navigation (v3): pill-style active item that lifts off the tinted sidebar,
 * icon in a soft tile, counts as sol pills. `variant` keeps framer-motion layoutIds unique.
 */
export const SidebarNav = ({ counts = {}, collapsed = false, onNavigate, variant = "desktop", section = "main" }) => {
  const { pathname } = useLocation();
  const go = useGuardedNavigate();
  const { isAdmin } = useAuth();
  const groups = navGroupsFor(isAdmin).filter((g) => (section === "footer" ? g.footer : !g.footer));
  const footer = section === "footer";

  return (
    <nav aria-label={footer ? "System" : "Admin-meny"} data-testid={footer ? "admin-sidebar-footer-nav" : "admin-sidebar-nav"} className={cn("flex flex-col", collapsed ? "gap-2" : footer ? "gap-0" : "gap-3.5")}>
      {groups.map((group, gi) => (
        <div key={group.label || "root"}>
          {collapsed ? (gi > 0 && <div className="mx-3 mb-2 border-t border-paper/10" aria-hidden="true" />) : (group.label && !footer && <p className="mb-1 px-3 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-paper/40">{group.label}</p>)}
          <ul className="space-y-[2px]">
            {group.items.map((item) => {
              const active = isNavActive(item, pathname);
              const count = item.badge ? counts[item.badge] || 0 : 0;
              const link = (
                <Link
                  to={item.to}
                  onClick={(e) => {
                    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
                    e.preventDefault();
                    go(item.to);
                    onNavigate?.();
                  }}
                  aria-current={active ? "page" : undefined}
                  aria-label={collapsed ? `${item.label}${count ? ` (${count})` : ""}` : undefined}
                  data-testid={`admin-nav-${item.id}`}
                  className={cn(
                    "group relative flex items-center gap-2.5 rounded-xl outline-none transition-[background-color,color] duration-150 focus-visible:ring-2 focus-visible:ring-sol/70", footer ? "h-8 text-[12.5px]" : "h-[34px] text-[13px]",
                    collapsed ? "justify-center px-0" : "px-2",
                    active ? "font-medium text-paper" : "text-paper/65 hover:bg-paper/[0.06] hover:text-paper",
                  )}
                >
                  {active && <motion.span layoutId={`admin-nav-active-${variant}-${section}`} className="admin-nav-active absolute inset-0 rounded-xl" transition={{ type: "spring", stiffness: 520, damping: 42 }} aria-hidden="true" />}
                  <span className={cn("relative flex shrink-0 items-center justify-center rounded-lg transition-colors", footer ? "h-6 w-7" : "h-7 w-7", active && !footer ? "bg-sol text-ink shadow-[0_0_0_3px_hsl(var(--sol)/0.18)]" : active ? "text-sol" : "text-paper/65 group-hover:text-paper")}>
                    <item.icon className={footer ? "h-4 w-4" : "h-[17px] w-[17px]"} strokeWidth={active ? 2 : 1.75} aria-hidden="true" />
                  </span>
                  {!collapsed && <span className="relative flex-1 truncate">{item.label}</span>}
                  {count > 0 && (collapsed
                    ? <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-sol ring-2 ring-ink" aria-hidden="true" />
                    : <span className="relative rounded-full bg-sol px-1.5 py-0.5 text-[11px] font-semibold leading-none text-ink tabular" data-testid={`admin-nav-${item.id}-badge`}>{count}</span>)}
                </Link>
              );
              return (
                <li key={item.to}>
                  {collapsed ? (
                    <Tooltip>
                      <TooltipTrigger asChild>{link}</TooltipTrigger>
                      <TooltipContent side="right" className="bg-paper text-ink">{item.label}{count ? ` · ${count}` : ""}</TooltipContent>
                    </Tooltip>
                  ) : link}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
};
